import { RefObject, useEffect, useRef, useState } from "react";

import { SPLIT_COLLAPSED_PX } from "./AuthSplitLayout.styles";

/**
 * Whether the container's content box is at or below SPLIT_COLLAPSED_PX,
 * measured with a ResizeObserver: the same box every `@container` rule reads.
 *
 * A viewport media query disagreed with those rules wherever the viewport and
 * the container differ: by `parentContainer`'s horizontal padding always, and
 * by a vertical scrollbar's width in split mode on windows under ~616px tall.
 *
 * State changes only when the boolean flips, not on every resize frame. It
 * starts `false`; until the first observer callback only the
 * `data-split-collapsed` attribute can be wrong, since the CSS decides the
 * layout and nothing acts on the flag before a side change.
 */
export function useContainerCollapsed<ContainerElement extends HTMLElement>(): [
  RefObject<ContainerElement | null>,
  boolean,
] {
  const containerRef = useRef<ContainerElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const containerNode = containerRef.current;
    if (!containerNode) return undefined;

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      const collapsed = entry.contentRect.width <= SPLIT_COLLAPSED_PX;
      setIsCollapsed((previousCollapsed) =>
        previousCollapsed === collapsed ? previousCollapsed : collapsed,
      );
    });

    observer.observe(containerNode);
    return () => observer.disconnect();
  }, []);

  return [containerRef, isCollapsed];
}
